import dayjs from 'dayjs';

import {
  EMPTY_OTHER_FAMILY,
  INITIAL_DISABILITY,
  INITIAL_HISTORY_LISTS,
  INITIAL_PREVENTIVE_CARE,
  INITIAL_PREGNANCY,
  emptyPriorDelivery,
  newOtherFamilyEntry,
  type PredefinedFamilyConditionCode,
} from '../constants';
import type {
  CurrentPregnancyData,
  DeliveryTypeCode,
  DisabilityCode,
  DisabilityState,
  FamilyConditionState,
  FamilyMemberCode,
  HistoryListState,
  HistorySection,
  NewbornSexCode,
  NewbornVitalStatusCode,
  ObstetricAnomalyType,
  OtherFamilyEntry,
  OtherFamilyState,
  PreventiveCareData,
  PregnancyTermStatusCode,
  PriorDeliveryRowState,
} from '../types';

export interface DossierForEdit {
  pregnancy?: Record<string, unknown> | null;
  family_history?: FamilyHistoryRow[] | null;
  prior_deliveries?: PriorDeliveryRow[] | null;
  patient_medical_history?: PatientMedicalHistoryRow | null;
}

interface FamilyHistoryRow {
  condition_code?: string | null;
  wife_family_notes?: string | null;
  husband_family_notes?: string | null;
}

interface PatientMedicalHistoryRow {
  medical_hx?: string | null;
  surgical_hx?: string | null;
  gynecological_hx?: string | null;
  disability_type?: string | null;
  disability_other_description?: string | null;
  vat_dose?: number | null;
  rubella_vaccinated?: boolean | null;
  rubella_vaccination_date?: string | null;
  hepatitis_b_vaccinated?: boolean | null;
  hepatitis_b_vaccination_date?: string | null;
  cervical_smear_iva_date?: string | null;
}

interface PriorDeliveryRow {
  id?: number | null;
  delivery_date?: string | null;
  delivery_type?: string | null;
  delivery_ga_weeks?: number | null;
  delivery_ga_days?: number | null;
  pregnancy_term_status?: string | null;
  extraction_modalities?: string | null;
  cesarean_indication?: string | null;
  delivery_place?: string | null;
  delivery_complication_type?: string | null;
  newborn_complication_type?: string | null;
  newborn_weight_grams?: number | null;
  newborn_sex?: string | null;
  newborn_vital_status?: string | null;
  has_obstetric_anomalies?: boolean | null;
  anomaly_type?: string | null;
  anomaly_date?: string | null;
  anomaly_place?: string | null;
  anomaly_ga_weeks?: number | null;
  anomaly_description?: string | null;
}

const PREDEFINED_FAMILY_CODES: PredefinedFamilyConditionCode[] = [
  'HTA',
  'DIABETES',
  'HEREDITARY_DISEASE',
  'MALFORMATION',
  'ALLERGY',
];

const FAMILY_MEMBER_CODES: FamilyMemberCode[] = [
  'MOTHER',
  'FATHER',
  'GRANDMOTHER',
  'GRANDFATHER',
  'SISTER',
  'BROTHER',
  'AUNT',
  'UNCLE',
  'COUSIN',
  'CHILD',
  'OTHER',
];

const DISABILITY_CODES: DisabilityCode[] = [
  'MOTOR',
  'SENSORY',
  'PSYCHIC',
  'MENTAL',
  'OTHER',
];

const DELIVERY_TYPE_CODES: DeliveryTypeCode[] = [
  'NORMAL',
  'CESAREAN',
  'INSTRUMENTAL',
  'OTHER',
];
const TERM_STATUS_CODES: PregnancyTermStatusCode[] = [
  'PRETERM',
  'TERM',
  'POSTTERM',
];
const NEWBORN_SEX_CODES: NewbornSexCode[] = ['M', 'F', 'UNKNOWN'];
const NEWBORN_VITAL_CODES: NewbornVitalStatusCode[] = [
  'ALIVE',
  'DECEASED_NEONATAL',
  'STILLBIRTH',
];
const ANOMALY_TYPE_CODES: ObstetricAnomalyType[] = [
  'AVORTEMENT',
  'IUFD',
  'CONGENITAL',
  'OTHER',
];

const isMemberCode = (s: string): s is FamilyMemberCode =>
  (FAMILY_MEMBER_CODES as string[]).includes(s);
const isDisabilityCode = (s: string): s is DisabilityCode =>
  (DISABILITY_CODES as string[]).includes(s);
const isDeliveryType = (s: string): s is DeliveryTypeCode =>
  (DELIVERY_TYPE_CODES as string[]).includes(s);
const isTermStatus = (s: string): s is PregnancyTermStatusCode =>
  (TERM_STATUS_CODES as string[]).includes(s);
const isNewbornSex = (s: string): s is NewbornSexCode =>
  (NEWBORN_SEX_CODES as string[]).includes(s);
const isNewbornVital = (s: string): s is NewbornVitalStatusCode =>
  (NEWBORN_VITAL_CODES as string[]).includes(s);
const isAnomalyType = (s: string): s is ObstetricAnomalyType =>
  (ANOMALY_TYPE_CODES as string[]).includes(s);

const decodeMembersFromNotes = (raw: unknown): FamilyMemberCode[] => {
  if (raw == null) {
    return [];
  }
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed[0] !== '{') {
      return [];
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (parsed && typeof parsed === 'object') {
    const members = (parsed as { members?: unknown }).members;
    if (Array.isArray(members)) {
      return members.filter(
        (m): m is FamilyMemberCode => typeof m === 'string' && isMemberCode(m)
      );
    }
  }
  return [];
};

interface OtherEntryNotes {
  label?: string;
  members?: FamilyMemberCode[];
}
const decodeOtherEntries = (raw: unknown): OtherEntryNotes[] => {
  if (raw == null) {
    return [];
  }
  let parsed: unknown = raw;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed[0] !== '{') {
      return [];
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }
  if (parsed && typeof parsed === 'object') {
    const entries = (parsed as { entries?: unknown }).entries;
    if (Array.isArray(entries)) {
      return entries
        .map((e): OtherEntryNotes | null => {
          if (!e || typeof e !== 'object') {
            return null;
          }
          const label =
            typeof (e as { label?: unknown }).label === 'string'
              ? ((e as { label: string }).label as string)
              : '';
          const rawMembers = (e as { members?: unknown }).members;
          const members = Array.isArray(rawMembers)
            ? rawMembers.filter(
                (m): m is FamilyMemberCode =>
                  typeof m === 'string' && isMemberCode(m)
              )
            : [];
          return { label, members };
        })
        .filter((e): e is OtherEntryNotes => e !== null);
    }
  }
  return [];
};

export const hydrateFamilyState = (
  rows: FamilyHistoryRow[] | null | undefined
): {
  family: Record<PredefinedFamilyConditionCode, FamilyConditionState>;
  otherFamily: OtherFamilyState;
} => {
  const family: Record<PredefinedFamilyConditionCode, FamilyConditionState> = {
    HTA: { active: false, wife_members: [], husband_members: [] },
    DIABETES: { active: false, wife_members: [], husband_members: [] },
    HEREDITARY_DISEASE: {
      active: false,
      wife_members: [],
      husband_members: [],
    },
    MALFORMATION: { active: false, wife_members: [], husband_members: [] },
    ALLERGY: { active: false, wife_members: [], husband_members: [] },
  };
  let otherFamily: OtherFamilyState = { ...EMPTY_OTHER_FAMILY };

  const list = Array.isArray(rows) ? rows : [];

  for (const row of list) {
    const code = row?.condition_code;
    if (!code) {
      continue;
    }
    if ((PREDEFINED_FAMILY_CODES as string[]).includes(code)) {
      const c = code as PredefinedFamilyConditionCode;
      const wife = decodeMembersFromNotes(row.wife_family_notes);
      const husband = decodeMembersFromNotes(row.husband_family_notes);
      family[c] = {
        active: true,
        wife_members: wife,
        husband_members: husband,
      };
    } else if (code === 'OTHER') {
      const wifeEntries = decodeOtherEntries(row.wife_family_notes);
      const husbandEntries = decodeOtherEntries(row.husband_family_notes);
      const labels = new Set<string>();
      wifeEntries.forEach((e) => e.label && labels.add(e.label));
      husbandEntries.forEach((e) => e.label && labels.add(e.label));
      const entries: OtherFamilyEntry[] = Array.from(labels).map((label) => {
        const w = wifeEntries.find((e) => e.label === label);
        const h = husbandEntries.find((e) => e.label === label);
        return {
          ...newOtherFamilyEntry(),
          label,
          wife_members: w?.members ?? [],
          husband_members: h?.members ?? [],
        };
      });
      otherFamily =
        entries.length > 0
          ? { active: true, entries }
          : { ...EMPTY_OTHER_FAMILY };
    }
  }

  return { family, otherFamily };
};

const splitTextEntries = (raw: string | null | undefined): string[] =>
  (raw ?? '')
    .split(/\r?\n|\u2022|;/g)
    .map((s) => s.trim())
    .filter(Boolean);

export const hydrateHistoryLists = (
  pmh: PatientMedicalHistoryRow | null | undefined
): Record<HistorySection, HistoryListState> => {
  const out: Record<HistorySection, HistoryListState> = {
    medical: { ...INITIAL_HISTORY_LISTS.medical, items: [''] },
    surgical: { ...INITIAL_HISTORY_LISTS.surgical, items: [''] },
    gynecological: { ...INITIAL_HISTORY_LISTS.gynecological, items: [''] },
  };
  if (!pmh) {
    return out;
  }

  const fill = (key: HistorySection, raw: string | null | undefined) => {
    const items = splitTextEntries(raw);
    if (items.length > 0) {
      out[key] = { active: true, items };
    }
  };
  fill('medical', pmh.medical_hx);
  fill('surgical', pmh.surgical_hx);
  fill('gynecological', pmh.gynecological_hx);
  return out;
};

export const hydrateDisability = (
  pmh: PatientMedicalHistoryRow | null | undefined
): DisabilityState => {
  if (!pmh) {
    return INITIAL_DISABILITY;
  }
  const codes = (pmh.disability_type ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .filter(isDisabilityCode);
  if (codes.length === 0) {
    return INITIAL_DISABILITY;
  }
  return {
    types: codes,
    other_description: pmh.disability_other_description ?? '',
  };
};

const toIsoDate = (value: unknown): string => {
  if (typeof value !== 'string' && !(value instanceof Date)) {
    return '';
  }
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

export const hydratePreventiveCare = (
  pmh: PatientMedicalHistoryRow | null | undefined
): PreventiveCareData => {
  if (!pmh) {
    return INITIAL_PREVENTIVE_CARE;
  }
  return {
    vat_dose:
      typeof pmh.vat_dose === 'number' && pmh.vat_dose >= 1 && pmh.vat_dose <= 5
        ? pmh.vat_dose
        : null,
    rubella_vaccinated: pmh.rubella_vaccinated ?? null,
    rubella_vaccination_date: toIsoDate(pmh.rubella_vaccination_date),
    hepatitis_b_vaccinated: pmh.hepatitis_b_vaccinated ?? null,
    hepatitis_b_vaccination_date: toIsoDate(
      pmh.hepatitis_b_vaccination_date
    ),
    cervical_smear_iva_date: toIsoDate(pmh.cervical_smear_iva_date),
  };
};

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const decodeAnomalyDescription = (
  raw: string | null | undefined
): { free: string; ga_days: number | null } => {
  const empty = { free: '', ga_days: null as number | null };
  if (!raw) {
    return empty;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return empty;
  }
  const m = trimmed.match(/^(.*?)\s*\[ga_days=(\d+)\]\s*$/);
  if (m) {
    const ga = Number(m[2]);
    return {
      free: m[1].trim(),
      ga_days: Number.isFinite(ga) ? ga : null,
    };
  }
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as {
        free?: string;
        ga_days?: number | null;
      };
      return {
        free: typeof parsed.free === 'string' ? parsed.free : '',
        ga_days:
          typeof parsed.ga_days === 'number' && Number.isFinite(parsed.ga_days)
            ? parsed.ga_days
            : null,
      };
    } catch {
      /* legacy plain text */
    }
  }
  return { free: trimmed, ga_days: null };
};

export interface PriorDeliveryHydrated extends PriorDeliveryRowState {
  id?: number | null;
}

export const hydratePriorDeliveries = (
  rows: PriorDeliveryRow[] | null | undefined
): PriorDeliveryHydrated[] => {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((row) => {
    const base = emptyPriorDelivery() as PriorDeliveryHydrated;
    base.id = row?.id ?? null;
    base.has_obstetric_anomalies = Boolean(row?.has_obstetric_anomalies);
    base.delivery_date = toIsoDate(row?.delivery_date);
    base.delivery_type =
      row?.delivery_type && isDeliveryType(row.delivery_type)
        ? row.delivery_type
        : '';
    base.delivery_ga_weeks = toNumberOrNull(row?.delivery_ga_weeks);
    base.delivery_ga_days = toNumberOrNull(row?.delivery_ga_days);
    base.pregnancy_term_status =
      row?.pregnancy_term_status && isTermStatus(row.pregnancy_term_status)
        ? row.pregnancy_term_status
        : '';
    base.extraction_modalities =
      typeof row?.extraction_modalities === 'string'
        ? row.extraction_modalities
        : '';
    base.cesarean_indication =
      typeof row?.cesarean_indication === 'string'
        ? row.cesarean_indication
        : '';
    base.delivery_place =
      typeof row?.delivery_place === 'string' ? row.delivery_place : '';
    base.delivery_complication_type =
      typeof row?.delivery_complication_type === 'string'
        ? row.delivery_complication_type
        : '';
    base.newborn_complication_type =
      typeof row?.newborn_complication_type === 'string'
        ? row.newborn_complication_type
        : '';
    base.newborn_weight_grams = toNumberOrNull(row?.newborn_weight_grams);
    base.newborn_sex =
      row?.newborn_sex && isNewbornSex(row.newborn_sex) ? row.newborn_sex : '';
    base.newborn_vital_status =
      row?.newborn_vital_status && isNewbornVital(row.newborn_vital_status)
        ? row.newborn_vital_status
        : '';
    base.anomaly_type =
      row?.anomaly_type && isAnomalyType(row.anomaly_type)
        ? row.anomaly_type
        : '';
    base.anomaly_date = toIsoDate(row?.anomaly_date);
    base.anomaly_place =
      typeof row?.anomaly_place === 'string' ? row.anomaly_place : '';
    base.anomaly_ga_weeks = toNumberOrNull(row?.anomaly_ga_weeks);
    const decoded = decodeAnomalyDescription(row?.anomaly_description);
    base.anomaly_description = decoded.free;
    base.anomaly_ga_days = decoded.ga_days;
    return base;
  });
};

export const hydratePregnancy = (
  pregnancy: Record<string, unknown> | null | undefined
): CurrentPregnancyData => {
  if (!pregnancy) {
    return INITIAL_PREGNANCY;
  }
  const lmp = toIsoDate(pregnancy.last_menstrual_period);
  const edd = toIsoDate(pregnancy.estimated_due_date);
  let gaWeeks = '';
  let gaDays = '';
  if (lmp) {
    const ref = pregnancy.ultrasound_date
      ? dayjs(pregnancy.ultrasound_date as string)
      : dayjs();
    if (dayjs(lmp).isValid()) {
      const diff = ref.startOf('day').diff(dayjs(lmp).startOf('day'), 'day');
      if (diff >= 0) {
        gaWeeks = String(Math.floor(diff / 7));
        gaDays = String(diff % 7);
      }
    }
  }
  return {
    grossesse_type:
      typeof pregnancy.grossesse_type === 'string'
        ? (pregnancy.grossesse_type as string)
        : '',
    grossesse_classee_a_risque:
      typeof pregnancy.grossesse_classee_a_risque === 'boolean'
        ? (pregnancy.grossesse_classee_a_risque as boolean)
        : null,
    risque_anemie: pregnancy.risque_anemie === true,
    risque_hta: pregnancy.risque_hta === true,
    risque_diabete: pregnancy.risque_diabete === true,
    risque_cardiopathie: pregnancy.risque_cardiopathie === true,
    risque_metrorragie: pregnancy.risque_metrorragie === true,
    risque_infection: pregnancy.risque_infection === true,
    risque_preeclampsie: pregnancy.risque_preeclampsie === true,
    risque_eclampsie: pregnancy.risque_eclampsie === true,
    risque_autre_details:
      typeof pregnancy.risque_autre_details === 'string'
        ? (pregnancy.risque_autre_details as string)
        : '',
    last_menstrual_period: lmp,
    estimated_due_date: edd,
    lmp_is_estimated: pregnancy.lmp_is_estimated === true,
    edd_is_estimated: pregnancy.edd_is_estimated === true,
    ga_weeks: gaWeeks,
    ga_days: gaDays,
    grossesse_desiree:
      typeof pregnancy.grossesse_desiree === 'boolean'
        ? (pregnancy.grossesse_desiree as boolean)
        : null,
    preg_crl: toNumberOrNull(pregnancy.preg_crl),
    preg_bpd: toNumberOrNull(pregnancy.preg_bpd),
    preg_hc: toNumberOrNull(pregnancy.preg_hc),
    preg_ac: toNumberOrNull(pregnancy.preg_ac),
    preg_fl: toNumberOrNull(pregnancy.preg_fl),
  };
};
